import { zodResolver } from '@hookform/resolvers/zod';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Stack from '@mui/material/Stack';
import { alpha } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { DialogTitle } from '@onesource/components';
import {
  CreateContentReportSchema,
  REPORT_DESCRIPTION_MAX_LENGTH,
  REPORT_REASON_HINTS,
  REPORT_REASON_LABELS,
  ReportReasonEnum,
  type TCreateContentReport,
} from '@onesource/schemas';
import { useReportContent, type TReportContentTarget } from '@onesource/services';
import { AxiosError } from 'axios';
import { FC, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { MdCheck, MdClose, MdOutlineFlag } from 'react-icons/md';

interface ReportContentDialogProps {
  open: boolean;
  onClose: () => void;
  targetType: TReportContentTarget;
  targetId: string;
}

const TARGET_LABEL: Record<TReportContentTarget, string> = {
  post: 'post',
  comment: 'reply',
};

const reasonOptionSx = {
  m: 0,
  px: 1.5,
  py: 0.75,
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: 1.5,
  cursor: 'pointer',
  transition: 'background-color 160ms ease-out, border-color 160ms ease-out, color 160ms ease-out',
  '&:hover': {
    borderColor: 'primary.main',
    bgcolor: (theme: { palette: { primary: { main: string } } }) => alpha(theme.palette.primary.main, 0.06),
  },
  '&:has(.Mui-checked)': {
    borderColor: 'primary.main',
    bgcolor: 'primary.main',
    color: 'primary.contrastText',
    '&:hover': { bgcolor: 'primary.dark' },
  },
  '&:has(.Mui-focusVisible)': {
    outline: '2px solid',
    outlineColor: 'primary.main',
    outlineOffset: 2,
  },
  '& .MuiFormControlLabel-label': { fontSize: 14, fontWeight: 600, lineHeight: 1.4 },
} as const;

// The native radio stays in the DOM (keyboard + screen readers) but the whole pill is the visible control.
const visuallyHiddenRadioSx = {
  position: 'absolute',
  width: 1,
  height: 1,
  m: -0.125,
  p: 0,
  opacity: 0,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
} as const;

const ReportSent: FC<{ targetLabel: string }> = ({ targetLabel }) => (
  <Stack alignItems="center" textAlign="center" spacing={1.5} sx={{ py: 3 }}>
    <Box
      sx={{
        width: 56,
        height: 56,
        display: 'grid',
        placeItems: 'center',
        borderRadius: '50%',
        color: 'success.contrastText',
        bgcolor: 'success.main',
        '@media (prefers-reduced-motion: no-preference)': {
          animation: 'report-sent-pop 420ms cubic-bezier(0.22, 1, 0.36, 1)',
        },
        '@keyframes report-sent-pop': {
          from: { transform: 'scale(0.6)', opacity: 0 },
          to: { transform: 'scale(1)', opacity: 1 },
        },
      }}
    >
      <MdCheck size={30} />
    </Box>
    <Typography variant="h6" component="p">
      Thanks for speaking up
    </Typography>
    <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 320 }}>
      Our moderators will review this {targetLabel}. The author won’t know it was you.
    </Typography>
  </Stack>
);

export const ReportContentDialog: FC<ReportContentDialogProps> = ({ open, onClose, targetType, targetId }) => {
  const mutation = useReportContent(targetType, targetId);
  const [submitted, setSubmitted] = useState(false);
  const targetLabel = TARGET_LABEL[targetType];

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<TCreateContentReport>({
    resolver: zodResolver(CreateContentReportSchema),
    defaultValues: { reason: undefined, description: '' },
    mode: 'onSubmit',
  });

  useEffect(() => {
    if (open) {
      reset({ reason: undefined, description: '' });
      mutation.reset();
      setSubmitted(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const reason = watch('reason');
  const description = watch('description') ?? '';

  const serverError =
    mutation.error instanceof AxiosError && mutation.error.response?.data?.message
      ? (mutation.error.response.data.message as string)
      : mutation.isError
        ? 'Could not submit your report. Please try again.'
        : null;

  const handleClose = () => {
    if (mutation.isPending) {
      return;
    }
    onClose();
  };

  const onSubmit = handleSubmit(async data => {
    try {
      await mutation.mutateAsync({ ...data, description: data.description || undefined });
      setSubmitted(true);
    } catch {
      // Error surfaced inline via `serverError`.
    }
  });

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle
        onClose={handleClose}
        icon={<MdOutlineFlag size={20} />}
        subtitle={submitted ? undefined : 'Your report is anonymous to the author.'}
      >
        {submitted ? 'Report sent' : `Report ${targetLabel}`}
      </DialogTitle>
      {submitted ? (
        <>
          <DialogContent dividers>
            <ReportSent targetLabel={targetLabel} />
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button variant="contained" startIcon={<MdCheck />} onClick={onClose} autoFocus>
              Done
            </Button>
          </DialogActions>
        </>
      ) : (
        <form onSubmit={onSubmit} noValidate>
          <DialogContent dividers>
            <Stack spacing={2.5}>
              {serverError && <Alert severity="error">{serverError}</Alert>}
              <Stack spacing={1.25}>
                <Typography variant="subtitle2" component="p" id="report-reason-label">
                  What’s wrong with this {targetLabel}?
                </Typography>
                <Controller
                  name="reason"
                  control={control}
                  render={({ field }) => (
                    <RadioGroup
                      {...field}
                      value={field.value ?? ''}
                      row
                      aria-labelledby="report-reason-label"
                      sx={{ gap: 1, flexWrap: 'wrap' }}
                    >
                      {ReportReasonEnum.options.map(option => (
                        <FormControlLabel
                          key={option}
                          value={option}
                          control={<Radio disableRipple sx={visuallyHiddenRadioSx} disabled={mutation.isPending} />}
                          label={REPORT_REASON_LABELS[option]}
                          sx={reasonOptionSx}
                        />
                      ))}
                    </RadioGroup>
                  )}
                />
                <Typography
                  variant="body2"
                  role={errors.reason ? 'alert' : undefined}
                  aria-live="polite"
                  color={errors.reason ? 'error' : 'text.secondary'}
                  sx={{ minHeight: 20 }}
                >
                  {errors.reason ? 'Pick the reason that fits best.' : reason ? REPORT_REASON_HINTS[reason] : null}
                </Typography>
              </Stack>
              <Controller
                name="description"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    value={field.value ?? ''}
                    label={reason === 'other' ? 'Describe the issue' : 'Anything else we should know? (optional)'}
                    multiline
                    minRows={3}
                    fullWidth
                    disabled={mutation.isPending}
                    error={!!errors.description}
                    helperText={
                      errors.description?.message ?? (
                        <Box component="span" sx={{ display: 'block', textAlign: 'right' }}>
                          {description.length}/{REPORT_DESCRIPTION_MAX_LENGTH}
                        </Box>
                      )
                    }
                    inputProps={{ maxLength: REPORT_DESCRIPTION_MAX_LENGTH }}
                  />
                )}
              />
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button variant="outlined" startIcon={<MdClose />} onClick={handleClose} disabled={mutation.isPending}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={mutation.isPending}
              startIcon={mutation.isPending ? <CircularProgress size={16} color="inherit" /> : <MdOutlineFlag />}
            >
              {mutation.isPending ? 'Sending…' : 'Submit report'}
            </Button>
          </DialogActions>
        </form>
      )}
    </Dialog>
  );
};
